/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/panel.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import i18next from 'i18next';
import panelConstants from '@components/central-monitoring/panels/add-panel/panel-constants';
import { FieldDefinitionFieldTypeDto } from '@interfaces-clean/field-rdl-definition.interface';
import { ExtraDataFields } from '@pages-clean/admin/study/admin-data-views/filters/types';
import { isPrimitive } from '@utils-clean/is-primitive';
import { useLocalState, useTranslation } from 'hooks';
import { GET_STUDY_VISIT_SCHEDULES } from 'queries/study-visit-schedules';
import {
    PanelOrderBy,
    PanelType,
    ProportionsOTBenchmarkType,
    EventRatesOTBenchmarkType,
    ModelScaleBy,
    RowDataSourceTag,
    StableVariableType,
} from 'types/graphql-global-types';
import Constants from 'utils/constants';
import PanelService from 'views/components/central-monitoring/panels/add-panel/service';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import AuditEnumValues from 'views/pages/admin/audit-log/definitions/enums';
import { handleAuditLogRecord } from 'views/pages/admin/audit-log/panel-type.service';
import {
    diffObjects,
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';
import { stringDateWithoutTimeFromLocal } from '@utils/convertor';
import { GET_EXTRA_FIELDS } from '@queries/central-monitoring';
import { panel_OpraQueries_panel_Panel } from '@queries/generated/central-monitoring.generated';
import {
    PanelListFields_Panel_proportionsOverTimeParameters_ProportionsOverTime as ProportionsOverTime,
    PanelListFields_Panel_eventRatesOverTimeParameters_EventRatesOverTime as EventRateOverTime,
    PanelListFields_Panel_triOpDateParameters_TriOpDateParameters as TriOpDateParameters,
    PanelListFields_Panel_timeline_Timeline as TimeLine,
    PanelListFields_Panel_timeline_Timeline_leftAxis_Axis as LeftAxis,
    AxisFields_Axis_dateSeries_DateSeries as DateSeries,
    AxisFields_Axis_dateSeries_DateSeries_rowData_SeriesRowData as DateSeriesRowData,
    AxisFields_Axis_numericSeries_NumericSeries as NumericSeries,
    AxisFields_Axis_numericSeries_NumericSeries_rowData_SeriesRowData as NumericSeriesRowData,
} from '@queries/generated/fragments.generated';
import { studyVisitSchedules, studyVisitSchedulesVariables } from '@queries/generated/study-visit-schedules.generated';

const propArray = ['ScheduleId', 'Name', 'CfgId', 'CfId'];

type StudyOverviewExtraColumns = {
    ColumnId: {
        $oid: string;
    };
    ColumnPosition: number;
    DataField: string;
    DataSource: number;
    DenominatorField: string;
    Label: string;
    NumeratorField: string;
    Type: number;
};

type ExtraDataFieldType = {
    fieldType: FieldDefinitionFieldTypeDto;
    sourceFieldName: string;
    table: string;
};

const PanelRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem }: any) => {
    const { t } = useTranslation();
    const { state } = useLocalState();

    const { data: extraFields } = useQuery<ExtraDataFields>(GET_EXTRA_FIELDS, {
        variables: {
            studyId: newItem?.studyId ?? '',
        },
        fetchPolicy: 'no-cache',
    });

    const { data: svts } = useQuery<studyVisitSchedules, studyVisitSchedulesVariables>(GET_STUDY_VISIT_SCHEDULES, {
        variables: {
            studyId: newItem?.studyId ?? '',
            timestamp: newItem?.updated ?? '',
        },
        fetchPolicy: 'no-cache',
    });
    const scheduleGraphTypes = [
        panelConstants.graphTypes.COLUMN,
        panelConstants.graphTypes.STACKED_COLUMN,
        panelConstants.graphTypes.HEATMAP,
    ];

    const getScheduleName = (record: any) => {
        if (
            (scheduleGraphTypes.includes(record?.graph?.type) &&
                record?.graph?.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER) ||
            (record?.graph?.type === panelConstants.graphTypes.SCATTER_PLOT_LINE &&
                record?.graph?.seriesType === panelConstants.seriesType.SUBJECT_BY_VISIT)
        ) {
            return svts?.studyVisitSchedules?.find((svt) => svt?.id === record.scheduleId)?.name;
        }
        return null;
    };

    let refactoredOldRecord = {
        ...oldRecord,
        PanelName: oldRecord.Name,
        scheduleName: getScheduleName(oldItem),
        ...removePropertiesFromObject(oldRecord, propArray),
        PanelType: oldItem ? PanelService.getPanelType(oldItem) : undefined,
        PanelTheme: oldItem ? PanelService.getPanelTheme(oldItem.panelTheme) : undefined,
        ConfigId: oldRecord.CfId?.$oid,
    };

    let refactoredNewRecord = {
        ...newRecord,
        PanelName: newRecord.Name,
        scheduleName: getScheduleName(newItem),
        ...removePropertiesFromObject(newRecord, propArray),
        PanelType: newItem ? PanelService.getPanelType(newItem) : undefined,
        PanelTheme: newItem ? PanelService.getPanelTheme(newItem.panelTheme) : undefined,
        ConfigId: newRecord.CfId?.$oid,
    };

    const result = handleAuditLogRecord({ oldItem, newItem, refactoredOldRecord, refactoredNewRecord });

    refactoredOldRecord = result.refactoredOldRecord;
    refactoredNewRecord = result.refactoredNewRecord;

    const dataSourceEnums = {
        RateByType: AuditEnumValues.DataSourceRateBy,
        TimePeriod: AuditEnumValues.GraphTimePeriod,
    };

    const dataSourceLookups = {
        XAxisEndDateFields: (value: any, recordData: any) => recordData?.XAxisEndDateFields?.join(', ') ?? '',
    };

    const dataSourceItems = getFieldChangeItems(
        refactoredOldRecord.DataSource,
        refactoredNewRecord.DataSource,
        null,
        (field: string) =>
            getProcessedFieldChangeItems({
                oldRecord: refactoredOldRecord.DataSource,
                newRecord: refactoredNewRecord.DataSource,
                oldItem: oldItem ? oldItem.DataSource : null,
                newItem: newItem ? newItem.DataSource : null,
                field,
                enums: dataSourceEnums,
                lookups: dataSourceLookups,
            }),
    );

    const graphEnums = {
        // fields from Graph object
        DisplayBy: AuditEnumValues.GraphDisplayBy,
        DisplayUnits: AuditEnumValues.GraphDisplayUnits,
        // TimePeriod: AuditEnumValues.GraphTimePeriod,
        XAxisTimePeriod: AuditEnumValues.GraphTimePeriod,
        SeriesType: AuditEnumValues.GraphSeries,
        OrderBy: AuditEnumValues.OrderBy,
        ColumnOrderBy: AuditEnumValues.OrderBy,
        ColumnType: AuditEnumValues.PanelColumnType,
        OrderByHeatmap: AuditEnumValues.OrderByHeatmap,
        LineThresholdType: AuditEnumValues.GraphLineThresholdType,
        LineCalculation: AuditEnumValues.GraphLineCalculation,
        CategoryRangePeriod: AuditEnumValues.GraphTimePeriod,
        ColumnRangeCategory: AuditEnumValues.ColumnRangeCategory,
        DataInclusionPeriod: AuditEnumValues.GraphTimePeriod,
        ColumnOrder: AuditEnumValues.OrderBy,
    };

    const arrayOfObjects = {
        Series: {
            valueResolver: (recordData: any, item: any, index: number) => {
                let value = '';
                if (recordData) {
                    const isHideIndex = recordData.DataPointField?.length && recordData.Label?.length === 0;
                    Object.keys(recordData).forEach((property) => {
                        if (
                            (recordData[property] &&
                                property.toLowerCase() === 'datapointfield' &&
                                recordData.Label.length === 0) ||
                            (recordData[property] && property.toLowerCase() !== 'datapointfield')
                        )
                            value += `${t(`auditLog:${property}-series`, {
                                number: !isHideIndex ? index + 1 : undefined,
                            })}: 
                                ${recordData[property] ?? 'none'}; `;
                    });
                }
                return value.length ? value : null;
            },
        },
        Categories: {
            valueResolver: (recordData: any, _item: any, _index: number) => {
                let value = '';
                if (recordData) {
                    Object.keys(recordData).forEach((property) => {
                        if (property.toLowerCase().match('clause')) {
                            if (recordData[property] > 0) {
                                const intClause = AuditEnumValues.PanelGraphCategoryClauseEnum[recordData[property]];
                                const clauseValue = Constants.clauses.find((clause: any) => clause.id === intClause);
                                value += `${property}: ${clauseValue?.text ?? ''}; `;
                            }
                        } else if (property.toLowerCase().match('min')) {
                            if (recordData.Clause > 0) {
                                value += `Value: ${recordData[property]}; `;
                            } else {
                                value += `${property}: ${recordData[property]}; `;
                            }
                        } else if (property.toLowerCase().match('max')) {
                            if (recordData.Clause === 0) {
                                value += `${property}: ${recordData[property]}; `;
                            }
                        } else {
                            value += `${property}: ${recordData[property]}; `;
                        }
                    });
                }
                return value.length ? value : null;
            },
        },
    };

    const refactoredOldRecordGraph = {
        ...refactoredOldRecord.Graph,
        Type: null,
    };

    const refactoredNewRecordGraph = {
        ...refactoredNewRecord.Graph,
        Type: null,
    };

    const graphItems = getFieldChangeItems(refactoredOldRecordGraph, refactoredNewRecordGraph, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecordGraph,
            newRecord: refactoredNewRecordGraph,
            oldItem: oldItem ? oldItem.Graph : null,
            newItem: newItem ? newItem.Graph : null,
            field,
            enums: graphEnums,
            arrayOfObjects,
            enumMarkerByField: {
                SeriesType: '_SERIES_TYPE',
            },
        }),
    );

    const modelParamsEnum: any = {
        StudyComparison: AuditEnumValues.ModelStudyComparison,
        DataInclusionPeriod: AuditEnumValues.ModelDataInclusionPeriod,
        CategoryRangePeriod: AuditEnumValues.ModelCategoryRangePeriod,
        SiteScoring: AuditEnumValues.ModelSiteScoring,
        ConfidenceIntervalType: AuditEnumValues.ModelConfidenceInterval,
        BenchmarkType: AuditEnumValues.ModelBenchmark,
        PlotBy: AuditEnumValues.ModelPlotBy,
        RateBy: AuditEnumValues.ModelRateBy,
        RateByTrend: AuditEnumValues.ModelRateBy,
        // RateGroupType: AuditEnumValues.RateGroupType,
        ShowHideThresholds: AuditEnumValues.ShowHideThresholds,
    };

    const refactoredOldRecordModelParams = {
        ...refactoredOldRecord.ModelParams,
        Model:
            oldItem && oldItem.panelType === PanelType.MODEL ? PanelService.getPanelModel(oldItem?.modelParams) : null,
        TRIThreshold1Type: null,
        TRIThreshold2Type: null,
        Type: null,
        RateGroupType: null,
    };
    const refactoredNewRecordModelParams = {
        ...refactoredNewRecord.ModelParams,
        Model:
            newItem && newItem.panelType === PanelType.MODEL ? PanelService.getPanelModel(newItem?.modelParams) : null,
        TRIThreshold1Type: null,
        TRIThreshold2Type: null,
        Type: null,
        RateGroupType: null,
    };

    const modelParamsItems = getFieldChangeItems(
        refactoredOldRecordModelParams,
        refactoredNewRecordModelParams,
        null,
        (field: string) =>
            getProcessedFieldChangeItems({
                oldRecord: refactoredOldRecordModelParams,
                newRecord: refactoredNewRecordModelParams,
                oldItem: oldItem ? oldItem.ModelParams : null,
                newItem: newItem ? newItem.ModelParams : null,
                field,
                enums: modelParamsEnum,
            }),
    );

    const isTriOpDate = (item: any) => {
        const panelField = item as panel_OpraQueries_panel_Panel;
        return panelField && panelField.panelType === PanelType.TRI_OP_DATE;
    };

    const isProportionsOT = (item: any) => {
        const panelField = item as panel_OpraQueries_panel_Panel;
        return panelField && panelField.panelType === PanelType.PROPORTIONS_OVER_TIME;
    };

    const isEventRatesOT = (item: any) => {
        const panelField = item as panel_OpraQueries_panel_Panel;
        return panelField && panelField.panelType === PanelType.EVENT_RATES_OVER_TIME;
    };

    const triOpEnumFields = [
        'dataInclusionPeriod',
        'thresholdCalculation',
        'stableVariableType',
        'siteAverage',
        'dataPopulation',
    ];

    const triProportionsOTEnumFields = [
        'scaleBy',
        'plotBy',
        'cumulativeOrTimePeriod',
        'thresholdType',
        'eligibilityPeriod',
        'benchmarkType',
        'countryBenchmark',
        'qtl',
        'reportOutOfTolerance',
        'automationRange',
    ];

    const rdlTables = ['rowData', 'timepointsList'];

    const rdlFields = ['rowDataDate', 'timepointDate', 'calcStartDate', 'rowDataFlag', 'yAxisDataField', 'sortField'];

    const triEventRatesOTEnumFields = [
        'scaleBy',
        'plotBy',
        'scaleTimePoint',
        'cumulativeOrTimePeriod',
        'thresholdType',
        'minTimeOnStudyPeriod',
        'benchmarkType',
        'countryBenchmark',
        'qtl',
        'reportOutOfTolerance',
        'rateBy',
        'automationRange',
    ];

    // Maps from cm2DataSource key to cm2DataSource UI name
    const cm2DataSourcesKeyNameMap = useMemo(() => {
        const cm2DataSourcesEntries = (state?.cm2DataSources ?? [])
            .filter((datasource) => !!datasource)
            .filter((datasource) => datasource?.tags && datasource.tags?.includes(RowDataSourceTag.PANEL))
            .map(({ id, name }) => [id, name] as const);

        return new Map(cm2DataSourcesEntries);
    }, [state]);

    // Maps from cm2Field key to cm2Field UI name
    const cm2FieldsKeyNameMap = useMemo(() => {
        const cm2FieldsEntries = (state?.cm2Fields ?? [])
            .filter((field) => !!field && !!field.fieldType)
            .map(({ key, name }) => [key, name] as const);

        return new Map(cm2FieldsEntries);
    }, [state]);

    const cm2FieldsByKeyMap = useMemo(() => {
        const cm2FieldsEntries = (state?.cm2Fields ?? [])
            .filter((field) => !!field && !!field.fieldType)
            .map((field) => [field.key, field] as const);

        return new Map(cm2FieldsEntries);
    }, [state]);

    const extraDataFieldsByKeyMap = useMemo(() => {
        const extraDataFieldsEntries = ((extraFields?.extraDataFields as unknown as ExtraDataFieldType[]) ?? [])
            .filter((field) => !!field && !!field.fieldType)
            .map(
                (field) =>
                    [
                        `${field.table
                            .toLowerCase()
                            .replace(/_+([a-z0-9])/g, (_, letter) => letter.toUpperCase())
                            .replace(/^./, (letter) => letter.toUpperCase())}.${field.sourceFieldName}` as string,
                        field,
                    ] as const,
            );

        return new Map(extraDataFieldsEntries);
    }, [extraFields?.extraDataFields]);

    const associations: any = {
        AssociatedRisks: {
            itemField: 'risks',
            name: 'Risk',
            nameResolver: (risk: any) => risk.statement,
        },
    };

    const newItems = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) => {
        if (field === 'Timeline') {
            function mapRuleValue(fieldKey: string | null | undefined, value: unknown) {
                let fieldType = fieldKey != null ? cm2FieldsByKeyMap.get(fieldKey)?.fieldType : null;

                // If extra data fields are used in rules then we need to get the fields to get their types.

                if (fieldType === null || fieldType === undefined) {
                    fieldType = fieldKey != null ? extraDataFieldsByKeyMap.get(fieldKey)?.fieldType : null;
                }

                if (fieldType == null || value == null || !isPrimitive(value)) {
                    return null;
                }

                switch (fieldType) {
                    case FieldDefinitionFieldTypeDto.TEXT:
                    case FieldDefinitionFieldTypeDto.INT:
                    case FieldDefinitionFieldTypeDto.LONG:
                    case FieldDefinitionFieldTypeDto.FLOAT:
                        return String(value);
                    case FieldDefinitionFieldTypeDto.BOOL:
                        // Check value is a boolean
                        if (typeof value === 'boolean') {
                            return t(`common:${value ? 'yes' : 'no'}`);
                        }
                        return null;
                    case FieldDefinitionFieldTypeDto.DATE:
                    case FieldDefinitionFieldTypeDto.PARTIAL_DATE:
                        // Check value is a string and represents valid date
                        if (typeof value === 'string' && !isNaN(new Date(value).getTime())) {
                            return stringDateWithoutTimeFromLocal(value, 'DD-MMM-yyyy');
                        }
                        return null;
                    default:
                        break;
                }

                return null;
            }

            function mapTimeline(timeline: TimeLine | undefined) {
                if (timeline == null) {
                    return undefined;
                }

                function mapRowData(rowData: NumericSeriesRowData | DateSeriesRowData) {
                    return {
                        ...rowData,
                        // Get name for key. Fallback to key.
                        source: cm2DataSourcesKeyNameMap.get(rowData.source) ?? rowData.source,
                        rule: {
                            ...rowData.rule,
                            field1:
                                rowData.rule?.field1 != null
                                    ? (cm2FieldsKeyNameMap.get(rowData.rule.field1) ?? rowData.rule?.field1)
                                    : null,
                            field2:
                                rowData.rule?.field2 != null
                                    ? (cm2FieldsKeyNameMap.get(rowData.rule.field2) ?? rowData.rule?.field2)
                                    : null,
                            logicalOperator:
                                rowData.rule?.logicalOperator != null
                                    ? t(`auditLog:${rowData.rule.logicalOperator}`)
                                    : null,
                            operator1: rowData.rule?.operator1 != null ? t(`auditLog:${rowData.rule.operator1}`) : null,
                            operator2: rowData.rule?.operator2 != null ? t(`auditLog:${rowData.rule.operator2}`) : null,
                            value1: mapRuleValue(rowData.rule?.field1, rowData.rule?.value1),
                            value2: mapRuleValue(rowData.rule?.field2, rowData.rule?.value2),
                        },
                    };
                }

                function mapNumericSeries(series: NumericSeries[] | null) {
                    if (series == null) {
                        return null;
                    }

                    return series.map(({ id, rowData, type, dateField, yPlotField, threshold, ...rest }) => ({
                        ...rest,
                        id: null,
                        type: t(`auditLog:${type}`),
                        dateField: cm2FieldsKeyNameMap.get(dateField) ?? dateField,
                        yPlotField: cm2FieldsKeyNameMap.get(yPlotField) ?? yPlotField,
                        threshold: {
                            ...threshold,
                            field:
                                threshold?.field != null
                                    ? {
                                          lower:
                                              threshold.field.lower != null
                                                  ? (cm2FieldsKeyNameMap.get(threshold.field.lower) ??
                                                    threshold.field.lower)
                                                  : null,
                                          upper:
                                              threshold.field.upper != null
                                                  ? (cm2FieldsKeyNameMap.get(threshold.field.upper) ??
                                                    threshold.field.upper)
                                                  : null,
                                      }
                                    : null,
                        },
                        rowData: mapRowData(rowData),
                    }));
                }

                function mapDateSeries(series: DateSeries[] | null) {
                    if (series == null) {
                        return null;
                    }

                    return series.map(({ id, rowData, type, plotLabel, startField, endField, ...rest }) => ({
                        ...rest,
                        id: null,
                        type: t(`auditLog:${type}`),
                        plotLabel: cm2FieldsKeyNameMap.get(plotLabel) ?? plotLabel,
                        startField: cm2FieldsKeyNameMap.get(startField) ?? startField,
                        endField: endField != null ? (cm2FieldsKeyNameMap.get(endField) ?? endField) : null,
                        rowData: mapRowData(rowData),
                    }));
                }

                const { leftAxis, rightAxis } = timeline;

                return {
                    ...timeline,
                    leftAxis: {
                        ...leftAxis,
                        type: t(`auditLog:field-timeline.enumValues.seriesType-${leftAxis.type}`),
                        numericSeries: mapNumericSeries(leftAxis.numericSeries),
                        dateSeries: mapDateSeries(leftAxis.dateSeries),
                    },
                    rightAxis:
                        rightAxis != null
                            ? {
                                  ...rightAxis,
                                  type: t(`auditLog:field-timeline.enumValues.seriesType-${rightAxis.type}`),
                                  numericSeries: mapNumericSeries(rightAxis.numericSeries),
                                  dateSeries: mapDateSeries(rightAxis.dateSeries),
                              }
                            : null,
                };
            }
            const oldPanelTimeline = mapTimeline(oldItem?.timeline);
            const newPanelTimeline = mapTimeline(newItem?.timeline);

            const changesWithRawFields = diffObjects(oldPanelTimeline, newPanelTimeline);

            return changesWithRawFields.map((change) => ({
                ...change,
                field: i18next.t(`auditLog:field-timeline.${change.field}`),
            }));
        }

        if (field.toLowerCase() === 'proportionsovertimeparameters') {
            const oldPanelParams = oldItem?.proportionsOverTimeParameters as ProportionsOverTime;
            const newPanelParams = newItem?.proportionsOverTimeParameters as ProportionsOverTime;
            const changesWithRawFields = diffObjects(oldPanelParams, newPanelParams);

            return changesWithRawFields.flatMap((change) => {
                if (change.field === 'studySpecificBenchmark') {
                    const studyType = newPanelParams?.benchmarkType;
                    if (studyType !== ProportionsOTBenchmarkType.STUDY_SPECIFIC) return [];
                }

                // Getting correct field names to display

                if (rdlFields.includes(change.field)) {
                    return [
                        {
                            oldValue:
                                change.oldValue !== undefined
                                    ? cm2FieldsKeyNameMap.get(change.oldValue as string)
                                    : undefined,
                            newValue:
                                change.newValue !== undefined
                                    ? cm2FieldsKeyNameMap.get(change.newValue as string)
                                    : undefined,
                            field: t(`auditLog:proportionsOT.field-${change.field}`),
                        },
                    ];
                }

                // Getting correct table names

                if (rdlTables.includes(change.field)) {
                    return [
                        {
                            oldValue:
                                change.oldValue !== undefined
                                    ? cm2DataSourcesKeyNameMap.get(change.oldValue as string)
                                    : undefined,
                            newValue:
                                change.newValue !== undefined
                                    ? cm2DataSourcesKeyNameMap.get(change.newValue as string)
                                    : undefined,
                            field: t(`auditLog:proportionsOT.field-${change.field}`),
                        },
                    ];
                }

                if (triProportionsOTEnumFields.includes(change.field)) {
                    // If it's an add and eligibility period is set blank from UI then it is saved as "None"
                    //  We don't need to audit it as the user hasn't changed it.
                    if (
                        change.field === 'eligibilityPeriod' &&
                        change.oldValue === undefined &&
                        change.newValue?.toString().toLowerCase() === 'none'
                    ) {
                        return [];
                    }
                    return [
                        {
                            oldValue:
                                change.oldValue != null
                                    ? t(`auditLog:proportionsOT.enums.${change.oldValue}`)
                                    : undefined,
                            newValue:
                                change.newValue != null
                                    ? t(`auditLog:proportionsOT.enums.${change.newValue}`)
                                    : undefined,
                            field: t(`auditLog:proportionsOT.field-${change.field}`),
                        },
                    ];
                }
                return [
                    {
                        ...change,
                        field: t(`auditLog:proportionsOT.field-${change.field}`),
                    },
                ];
            });
        }

        if (field.toLowerCase() === 'eventratesovertimeparameters') {
            const oldPanelParams = oldItem?.eventRatesOverTimeParameters as EventRateOverTime;
            const newPanelParams = newItem?.eventRatesOverTimeParameters as EventRateOverTime;
            const changesWithRawFields = diffObjects(oldPanelParams, newPanelParams);

            const subChanges = changesWithRawFields.flatMap((change) => {
                if (change.field === 'studySpecificBenchmark') {
                    const studyType = newPanelParams?.benchmarkType;
                    if (studyType !== EventRatesOTBenchmarkType.STUDY_SPECIFIC) return [];
                }

                // Getting calcEndDates at the end of this block
                if (change.field.startsWith('calcEndDates')) {
                    return [];
                }

                // Backend stores scaleBy but UI displays it in 3 fields. RateBy, ScaleBy, Scale TimePoint.
                // RateBy displays TimePoints or Period depending on value we are getting for Scale By.
                // ScaleBy will display the value from ModelScaleBy enums if it is not TimePoint.
                // Scale TimePoint will display the value from ModelScaleBy enums if it is TimePoint.

                if (change.field === 'scaleBy') {
                    const newRateBy = newPanelParams?.scaleBy;
                    const oldRateBy = oldPanelParams?.scaleBy;

                    const isOldRateByTimePoint =
                        oldRateBy === ModelScaleBy.PER_SUBJECT_TIME_POINT ||
                        oldRateBy === ModelScaleBy.PER_10_SUBJECT_TIME_POINTS ||
                        oldRateBy === ModelScaleBy.PER_100_SUBJECT_TIME_POINTS ||
                        oldRateBy === ModelScaleBy.PER_1000_SUBJECT_TIME_POINTS;

                    const isNewRateByTimePoint =
                        newRateBy === ModelScaleBy.PER_SUBJECT_TIME_POINT ||
                        newRateBy === ModelScaleBy.PER_10_SUBJECT_TIME_POINTS ||
                        newRateBy === ModelScaleBy.PER_100_SUBJECT_TIME_POINTS ||
                        newRateBy === ModelScaleBy.PER_1000_SUBJECT_TIME_POINTS;

                    return [
                        {
                            oldValue:
                                change.oldValue !== undefined
                                    ? isOldRateByTimePoint
                                        ? t('auditLog:evenRatesOT.enums.TIMEPOINTS')
                                        : t('auditLog:evenRatesOT.enums.PERIOD')
                                    : undefined,
                            newValue:
                                change.newValue !== undefined
                                    ? isNewRateByTimePoint
                                        ? t('auditLog:evenRatesOT.enums.TIMEPOINTS')
                                        : t('auditLog:evenRatesOT.enums.PERIOD')
                                    : undefined,
                            field: t('auditLog:evenRatesOT.field-rateBy'),
                        },
                        ...(oldRateBy !== undefined || !isNewRateByTimePoint
                            ? [
                                  {
                                      oldValue:
                                          change.oldValue !== undefined
                                              ? isOldRateByTimePoint
                                                  ? undefined
                                                  : t(`auditLog:evenRatesOT.enums.${change.oldValue}`)
                                              : undefined,
                                      newValue:
                                          change.newValue !== undefined
                                              ? isNewRateByTimePoint
                                                  ? undefined
                                                  : t(`auditLog:evenRatesOT.enums.${change.newValue}`)
                                              : undefined,
                                      field: t('auditLog:evenRatesOT.field-scaleBy'),
                                  },
                              ]
                            : []),

                        ...(oldRateBy !== undefined || isNewRateByTimePoint
                            ? [
                                  {
                                      oldValue:
                                          change.oldValue !== undefined
                                              ? !isOldRateByTimePoint
                                                  ? undefined
                                                  : t(`auditLog:evenRatesOT.enums.${change.oldValue}`)
                                              : undefined,
                                      newValue:
                                          change.newValue !== undefined
                                              ? !isNewRateByTimePoint
                                                  ? undefined
                                                  : t(`auditLog:evenRatesOT.enums.${change.newValue}`)
                                              : undefined,
                                      field: t('auditLog:evenRatesOT.field-scaleTimepoint'),
                                  },
                              ]
                            : []),
                    ];
                }

                // Getting correct enum values

                if (triEventRatesOTEnumFields.includes(change.field)) {
                    // If it's an add and eligibility period is set blank from UI then it is saved as "None"
                    //  We don't need to audit it as the user hasn't changed it.

                    if (
                        change.field === 'minTimeOnStudyPeriod' &&
                        change.oldValue === undefined &&
                        change.newValue?.toString().toLowerCase() === 'none'
                    ) {
                        return [];
                    }

                    return [
                        {
                            oldValue:
                                change.oldValue != null
                                    ? t(`auditLog:evenRatesOT.enums.${change.oldValue}`)
                                    : undefined,
                            newValue:
                                change.newValue != null
                                    ? t(`auditLog:evenRatesOT.enums.${change.newValue}`)
                                    : undefined,
                            field: t(`auditLog:evenRatesOT.field-${change.field}`),
                        },
                    ];
                }

                // Getting correct field names to display

                if (rdlFields.includes(change.field)) {
                    return [
                        {
                            oldValue:
                                change.oldValue !== undefined
                                    ? cm2FieldsKeyNameMap.get(change.oldValue as string)
                                    : undefined,
                            newValue:
                                change.newValue !== undefined
                                    ? cm2FieldsKeyNameMap.get(change.newValue as string)
                                    : undefined,
                            field: t(`auditLog:evenRatesOT.field-${change.field}`),
                        },
                    ];
                }

                // Getting correct table names

                if (rdlTables.includes(change.field)) {
                    return [
                        {
                            oldValue:
                                change.oldValue !== undefined
                                    ? cm2DataSourcesKeyNameMap.get(change.oldValue as string)
                                    : undefined,
                            newValue:
                                change.newValue !== undefined
                                    ? cm2DataSourcesKeyNameMap.get(change.newValue as string)
                                    : undefined,
                            field: t(`auditLog:evenRatesOT.field-${change.field}`),
                        },
                    ];
                }

                return [
                    {
                        ...change,
                        field: t(`auditLog:evenRatesOT.field-${change.field}`),
                    },
                ];
            });

            // calcEndDates is a string array, and we need to display it in csv string.
            if (changesWithRawFields.find((item) => item.field.startsWith('calcEndDates'))) {
                const newCalcEndDates = newPanelParams?.calcEndDates?.map((d) => cm2FieldsKeyNameMap.get(d as string));
                const oldCalcEndDates = oldPanelParams?.calcEndDates?.map((d) => cm2FieldsKeyNameMap.get(d as string));

                subChanges.push({
                    oldValue: oldCalcEndDates !== undefined ? oldCalcEndDates?.join(', ') : undefined,
                    newValue: newCalcEndDates !== undefined ? newCalcEndDates?.join(', ') : undefined,
                    field: t('auditLog:evenRatesOT.field-calcEndDates'),
                });
            }

            return subChanges;
        }

        if (field.toLowerCase().match(/triopdateparameters/)) {
            const oldPanelParams = oldItem?.triOpDateParameters as TriOpDateParameters;
            const newPanelParams = newItem?.triOpDateParameters as TriOpDateParameters;
            const changesWithRawFields = diffObjects(oldPanelParams, newPanelParams);

            const subChanges = changesWithRawFields.flatMap((change) => {
                if (change.field === 'stableValue') return [];
                if (triOpEnumFields.includes(change.field)) {
                    return [
                        {
                            oldValue: change.oldValue ? t(`auditLog:triOpDate.enums.${change.oldValue}`) : undefined,
                            newValue: change.newValue ? t(`auditLog:triOpDate.enums.${change.newValue}`) : undefined,
                            field: t(`auditLog:triOpDate.field-${change.field}`),
                        },
                    ];
                }

                // Getting correct field names to display

                if (rdlFields.includes(change.field)) {
                    return [
                        {
                            oldValue:
                                change.oldValue !== undefined
                                    ? cm2FieldsKeyNameMap.get(change.oldValue as string)
                                    : undefined,
                            newValue:
                                change.newValue !== undefined
                                    ? cm2FieldsKeyNameMap.get(change.newValue as string)
                                    : undefined,
                            field: t(`auditLog:triOpDate.field-${change.field}`),
                        },
                    ];
                }

                // Getting correct table names

                if (rdlTables.includes(change.field)) {
                    return [
                        {
                            oldValue:
                                change.oldValue !== undefined
                                    ? cm2DataSourcesKeyNameMap.get(change.oldValue as string)
                                    : undefined,
                            newValue:
                                change.newValue !== undefined
                                    ? cm2DataSourcesKeyNameMap.get(change.newValue as string)
                                    : undefined,
                            field: t(`auditLog:triOpDate.field-${change.field}`),
                        },
                    ];
                }

                return [
                    {
                        ...change,
                        field: t(`auditLog:triOpDate.field-${change.field}`),
                    },
                ];
            });

            // Add complex stable number/percentage fields
            const oldType = oldPanelParams?.stableVariableType;
            const newType = newPanelParams?.stableVariableType;
            const oldStableValue = oldPanelParams?.stableValue;
            const newStableValue = newPanelParams?.stableValue;
            if (oldType === StableVariableType.NUMBER || newType === StableVariableType.NUMBER) {
                if (oldType !== newType || oldStableValue !== newStableValue) {
                    subChanges.push({
                        oldValue: oldType === StableVariableType.NUMBER ? oldStableValue : undefined,
                        newValue: newType === StableVariableType.NUMBER ? newStableValue : undefined,
                        field: t('auditLog:triOpDate.field-stableNumber'),
                    });
                }
            }
            if (oldType === StableVariableType.PERCENTAGE || newType === StableVariableType.PERCENTAGE)
                if (oldType !== newType || oldStableValue !== newStableValue) {
                    subChanges.push({
                        oldValue: oldType === StableVariableType.PERCENTAGE ? oldStableValue : undefined,
                        newValue: newType === StableVariableType.PERCENTAGE ? newStableValue : undefined,
                        field: t('auditLog:triOpDate.field-stablePercentage'),
                    });
                }
            return subChanges;
        }

        if (
            (oldRecord &&
                !isTriOpDate(oldItem) &&
                !isProportionsOT(oldItem) &&
                !isEventRatesOT(oldItem) &&
                oldRecord[field] &&
                Object.keys(oldRecord[field]).length) ||
            (newRecord &&
                !isTriOpDate(newItem) &&
                !isProportionsOT(newItem) &&
                !isEventRatesOT(newItem) &&
                newRecord[field] &&
                Object.keys(newRecord[field]).length)
        ) {
            if (field.toLowerCase().match(/graph/)) return graphItems;
            if (field.toLowerCase().match(/modelparams/)) return modelParamsItems;
            if (field.toLowerCase().match(/datasource/)) return dataSourceItems;
        }

        return getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            arrayOfObjects: {
                StudyOverviewExtraColumns: {
                    valueResolver: (recordData: StudyOverviewExtraColumns) => {
                        let value: string | null = null;
                        if (recordData) {
                            value = `${t('auditLog:studyOverviewExtraColumns.Column')} ${recordData.ColumnPosition},
                                ${t('auditLog:studyOverviewExtraColumns.Label')}: ${recordData.Label},
                                ${t('auditLog:studyOverviewExtraColumns.Type')}: ${t(`auditLog:studyOverviewExtraColumns.Type-${recordData.Type}`)},
                                ${t('auditLog:studyOverviewExtraColumns.DataSource')}: ${Constants.DataSourceLOV.find((d) => d.value === recordData.DataSource)?.key},
                                ${t('auditLog:studyOverviewExtraColumns.DataPointCalculation')}: ${recordData.DataField || 'N/A'},
                                ${t('auditLog:studyOverviewExtraColumns.Denominator')}: ${recordData.DenominatorField || 'N/A'},
                                ${t('auditLog:studyOverviewExtraColumns.Numerator')}: ${recordData.NumeratorField || 'N/A'},`;
                        }
                        return value;
                    },
                },
            },
            associations,
        });
    });

    return <FieldChangeGrid items={newItems} />;
};

export { PanelRecordChangeGrid };
